import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Search, Filter, Edit, Trash2, Plus, Download, RefreshCw, CheckCircle, XCircle, AlertCircle, ExternalLink } from 'lucide-react';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import productService from '../../services/productService';

const AdminProductManagement = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin-login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && !authLoading) {
      loadProducts();
    }
  }, [user, authLoading]);

  useEffect(() => {
    applyFilters();
  }, [searchQuery, selectedCategory, selectedBrand, priceRange, products]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await productService?.getAllProducts();
      setProducts(data || []);
      setFilteredProducts(data || []);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Search filter
    if (searchQuery) {
      filtered = filtered?.filter(product =>
        product?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        product?.brand?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        product?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered?.filter(product => product?.category === selectedCategory);
    }

    // Brand filter
    if (selectedBrand !== 'all') {
      filtered = filtered?.filter(product => product?.brand === selectedBrand);
    }

    // Price range filter
    if (priceRange?.min) {
      filtered = filtered?.filter(product => product?.currentPrice >= parseFloat(priceRange?.min));
    }
    if (priceRange?.max) {
      filtered = filtered?.filter(product => product?.currentPrice <= parseFloat(priceRange?.max));
    }

    setFilteredProducts(filtered);
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev =>
      prev?.includes(productId)
        ? prev?.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProducts?.length === filteredProducts?.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts?.map(p => p?.id));
    }
  };

  const handleEditProduct = (productId) => {
    navigate(`/admin/products/${productId}/edit`);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      // Implement delete functionality
      console.log('Delete product:', productId);
      await loadProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const handleExportData = () => {
    const csvData = filteredProducts?.map(p => ({
      Name: p?.name,
      Brand: p?.brand,
      Category: p?.category,
      Price: p?.currentPrice,
      Rating: p?.rating,
      Reviews: p?.reviewCount,
      Status: p?.isActive ? 'Active' : 'Inactive'
    }));

    const csv = [
      Object.keys(csvData?.[0])?.join(','),
      ...csvData?.map(row => Object.values(row)?.join(','))
    ]?.join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `products-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
  };

  const getStatusIcon = (product) => {
    const hasReview = product?.reviewCount > 0;
    const hasAffiliateLinks = product?.affiliateLinks && product?.affiliateLinks?.length > 0;
    const hasImage = product?.imageUrl && product?.imageUrl !== '/assets/images/no_image.png';

    if (hasReview && hasAffiliateLinks && hasImage) {
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    } else if (!hasReview || !hasAffiliateLinks || !hasImage) {
      return <AlertCircle className="w-5 h-5 text-yellow-600" />;
    } else {
      return <XCircle className="w-5 h-5 text-red-600" />;
    }
  };

  const uniqueBrands = [...new Set(products?.map(p => p?.brand))]?.sort();
  const categories = [
    { value: 'upright', label: 'Upright' },
    { value: 'robot', label: 'Robot' },
    { value: 'cordless_stick', label: 'Cordless Stick' },
    { value: 'canister', label: 'Canister' },
    { value: 'handheld', label: 'Handheld' },
    { value: 'stick', label: 'Stick' }
  ];

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Product Management</h1>
              <p className="text-gray-600 mt-2">
                Manage your product catalog with advanced filtering and bulk operations
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={loadProducts}
                variant="outline"
                className="flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
              <Button
                onClick={handleExportData}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
              <Button
                onClick={() => navigate('/admin/ai-reviews')}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Product
              </Button>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filters
            </h2>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="ghost"
              size="sm"
            >
              {showFilters ? 'Hide' : 'Show'} Advanced Filters
            </Button>
          </div>

          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search by name, brand, or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="pl-10"
              />
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e?.target?.value)}
                >
                  <option value="all">All Categories</option>
                  {categories?.map(cat => (
                    <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                  ))}
                </Select>

                <Select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e?.target?.value)}
                >
                  <option value="all">All Brands</option>
                  {uniqueBrands?.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </Select>

                <Input
                  type="number"
                  placeholder="Min Price"
                  value={priceRange?.min}
                  onChange={(e) => setPriceRange(prev => ({ ...prev, min: e?.target?.value }))}
                />

                <Input
                  type="number"
                  placeholder="Max Price"
                  value={priceRange?.max}
                  onChange={(e) => setPriceRange(prev => ({ ...prev, max: e?.target?.value }))}
                />
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>
              Showing {filteredProducts?.length} of {products?.length} products
            </span>
            {selectedProducts?.length > 0 && (
              <span className="font-medium text-indigo-600">
                {selectedProducts?.length} selected
              </span>
            )}
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading products...</p>
            </div>
          ) : filteredProducts?.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-600">No products found matching your filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <Checkbox
                        checked={selectedProducts?.length === filteredProducts?.length}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Brand
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rating
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reviews
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProducts?.map((product) => (
                    <tr key={product?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Checkbox
                          checked={selectedProducts?.includes(product?.id)}
                          onChange={() => handleSelectProduct(product?.id)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusIcon(product)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <img
                            src={product?.imageUrl || '/assets/images/no_image.png'}
                            alt={product?.imageAlt || product?.name}
                            className="w-12 h-12 rounded object-cover"
                            onError={(e) => {
                              e.target.src = '/assets/images/no_image.png';
                            }}
                          />
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {product?.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {product?.slug}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {product?.brand}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">
                          {product?.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${product?.currentPrice?.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {product?.rating?.toFixed(1)} ⭐
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {product?.reviewCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => handleEditProduct(product?.id)}
                            variant="ghost"
                            size="sm"
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => window.open(`/product-review/${product?.slug}`, '_blank')}
                            variant="ghost"
                            size="sm"
                            className="text-gray-600 hover:text-gray-900"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleDeleteProduct(product?.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminProductManagement;